import React, { useEffect, useState } from "react";
import { Card, Table, Button, Form, Badge, Modal, Row, Col, Tabs, Tab } from "react-bootstrap";
import {
  getAllSalaryConfigs,
  getAllSalaryRecords,
  generateBulkSalaryRecords,
  updateSalaryRecordStatus,
  getSalaryStatistics,
  deleteSalaryConfig,
  getEligibleUsersForSalary,
  recordSalaryPayment,
  getSalaryPayments,
  formatCurrency,
  getMonthName,
  SALARY_TYPES,
  PAYMENT_STATUS,
  PAYMENT_METHODS,
} from "../services/salaryService";
import Loader from "../components/Loader";
import SalaryForm from "../components/SalaryForm";
import SalaryCard from "../components/SalaryCard";
import { toast } from "react-toastify";
import { EditIcon, TrashIcon } from "lucide-react";

export default function Salary() {
  const [salaryConfigs, setSalaryConfigs] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("configs");

  // Filters
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth() + 1);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("");

  // Modal states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingSalaryConfig, setEditingSalaryConfig] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Payment form state
  const [paymentFormData, setPaymentFormData] = useState({
    amount: "",
    paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
    paymentDate: new Date().toISOString().split("T")[0],
    referenceNumber: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "records") {
      fetchSalaryRecords();
    } else if (activeTab === "statistics") {
      fetchStatistics();
    }
  }, [activeTab, monthFilter, yearFilter, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configs, eligibleUsers] = await Promise.all([
        getAllSalaryConfigs(),
        getEligibleUsersForSalary(),
      ]);

      setSalaryConfigs(configs);
      setAllUsers(eligibleUsers); // Now includes tutors, employees, and individual interns only
    } catch (error) {
      toast.error("Failed to fetch salary data");
      console.error(error);
    }
    setLoading(false);
  };

  const fetchSalaryRecords = async () => {
    try {
      const filters = {};
      if (monthFilter) filters.month = monthFilter;
      if (yearFilter) filters.year = yearFilter;
      if (statusFilter) filters.status = statusFilter;

      const records = await getAllSalaryRecords(filters);

      // Calculate working days (exclude weekends)
      const getWorkingDays = (year, month) => {
        const days = [];
        const date = new Date(year, month - 1, 1);
        while (date.getMonth() === month - 1) {
          const day = date.getDay();
          if (day !== 0 && day !== 6) days.push(new Date(date)); // 0: Sunday, 6: Saturday
          date.setDate(date.getDate() + 1);
        }
        return days;
      };

      // Update salary records with new calculation
      const updatedRecords = records.map((record) => {
        const workingDays = getWorkingDays(record.year, record.month);
        const perDaySalary = (record.grossSalary || 0) / workingDays.length;
        // Only count days with attendance >7 hours or approved leave
        const eligibleDays = (record.attendanceRecords || []).filter(
          (a) =>
            (a.totalHours && a.totalHours >= 7) ||
            (a.leaveStatus && a.leaveStatus === 'approved')
        ).length;
        const netSalary = perDaySalary * eligibleDays;
        return {
          ...record,
          workingDays: workingDays.length,
          eligibleDays,
          perDaySalary,
          netSalary,
        };
      });
      setSalaryRecords(updatedRecords);
    } catch (error) {
      toast.error("Failed to fetch salary records");
      console.error(error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const filters = {};
      if (monthFilter) filters.month = monthFilter;
      if (yearFilter) filters.year = yearFilter;

      const stats = await getSalaryStatistics(filters);
      setStatistics(stats);
    } catch (error) {
      toast.error("Failed to fetch salary statistics");
      console.error(error);
    }
  };

  const handleBulkGenerate = async () => {
    try {
      setLoading(true);
      const results = await generateBulkSalaryRecords(
        monthFilter,
        yearFilter,
        ['tutor', 'employee', 'individual_intern']
      );

      if (results.success.length > 0) {
        toast.success(`Generated salary records for ${results.success.length} employees`);
        fetchSalaryRecords();
      }

      if (results.errors.length > 0) {
        toast.warning(`${results.errors.length} records failed to generate. Check console for details.`);
      }
    } catch (error) {
      toast.error("Failed to generate bulk salary records");
      console.error(error);
    }
    setLoading(false);
    setShowBulkModal(false);
  };

  const handleStatusUpdate = async () => {
    try {
      await updateSalaryRecordStatus(
        selectedRecord.id,
        newStatus,
        newStatus === PAYMENT_STATUS.PAID ? parseFloat(paidAmount) : null
      );

      fetchSalaryRecords();
      setShowStatusModal(false);
      setSelectedRecord(null);
      setNewStatus("");
      setPaidAmount("");
    } catch (error) {
      toast.error("Failed to update salary record");
      console.error(error);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (window.confirm("Are you sure you want to delete this salary configuration?")) {
      try {
        await deleteSalaryConfig(configId);
        fetchData();
      } catch (error) {
        toast.error("Failed to delete salary configuration");
        console.error(error);
      }
    }
  };

  const handleEditConfig = (config) => {
    setEditingSalaryConfig(config);
    setShowSalaryForm(true);
  };

  const handleSalaryFormSuccess = () => {
    fetchData();
    setEditingSalaryConfig(null);
  };

  const handleRecordPayment = async () => {
    try {
      if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
        toast.error("Please enter a valid payment amount");
        return;
      }

      const remainingAmount = selectedRecord.netSalary - (selectedRecord.totalPaid || 0);
      if (parseFloat(paymentFormData.amount) > remainingAmount) {
        toast.error(`Payment amount cannot exceed remaining amount of ${formatCurrency(remainingAmount)}`);
        return;
      }

      await recordSalaryPayment({
        recordId: selectedRecord.id,
        userId: selectedRecord.userId,
        userName: selectedRecord.userName,
        ...paymentFormData,
      });

      // Reset form
      setPaymentFormData({
        amount: "",
        paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
        paymentDate: new Date().toISOString().split("T")[0],
        referenceNumber: "",
        notes: "",
      });

      setShowPaymentModal(false);
      setSelectedRecord(null);
      fetchSalaryRecords();
    } catch (error) {
      console.error("Error recording payment:", error);
    }
  };

  const handleViewPaymentHistory = async (record) => {
    try {
      setSelectedRecord(record);
      const payments = await getSalaryPayments(record.id);
      setPaymentHistory(payments);
      setShowPaymentHistoryModal(true);
    } catch (error) {
      toast.error("Failed to fetch payment history");
      console.error(error);
    }
  };

  const getUserName = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    return user ? user.name : "Unknown User";
  };

  const getUserRole = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    return user ? (user.displayRole || user.role) : "Unknown";
  };

  const getUserLevel = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    return user?.studentLevel || user?.displayRole || "Unknown";
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      [PAYMENT_STATUS.PENDING]: "warning",
      [PAYMENT_STATUS.PAID]: "success",
      [PAYMENT_STATUS.PARTIALLY_PAID]: "info",
      [PAYMENT_STATUS.OVERDUE]: "danger",
      [PAYMENT_STATUS.CANCELLED]: "secondary",
    };

    return <Badge bg={statusColors[status] || "secondary"}>{status.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getSalaryTypeBadge = (type) => {
    const typeColors = {
      [SALARY_TYPES.MONTHLY]: "primary",
      [SALARY_TYPES.HOURLY]: "info",
      [SALARY_TYPES.PROJECT_BASED]: "warning",
      [SALARY_TYPES.INTERN_STIPEND]: "secondary",
    };

    return <Badge bg={typeColors[type] || "secondary"}>{type.replace('_', ' ').toUpperCase()}</Badge>;
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-2xl font-bold">Salary Management</h2>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => setShowBulkModal(true)}
            disabled={loading}
          >
            Generate Bulk Records
          </Button>
        </div>
      </div>

      {loading && <Loader />}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="configs" title="Salary Configurations">
          <Card className="shadow">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title>User Salary Configurations</Card.Title>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowSalaryForm(true)}
                >
                  Add New Configuration
                </Button>
              </div>

              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>User Name</th>
                    <th>Role</th>
                    <th>Salary Type</th>
                    <th>Base Salary</th>
                    <th>Allowances</th>
                    <th>Overtime Rate</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryConfigs.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No salary configurations found
                      </td>
                    </tr>
                  ) : (
                    salaryConfigs.map((config, index) => (
                      <tr key={config.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{getUserName(config.userId)}</strong>
                        </td>
                        <td>
                          <Badge
                            bg={
                              getUserLevel(config.userId) === 'tutor' ? 'primary' : getUserLevel(config.userId) === 'employee' ? 'warning' :
                                'secondary'
                            }
                          >
                            {getUserLevel(config.userId).replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td>{getSalaryTypeBadge(config.salaryType)}</td>
                        <td>{formatCurrency(config.baseSalary)}</td>
                        <td>{formatCurrency(config.allowances || 0)}</td>
                        <td>{config.overtimeRate ? formatCurrency(config.overtimeRate) + "/hr" : "N/A"}</td>
                        <td>{config.createdAt?.toLocaleDateString()}</td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditConfig(config)}
                          >
                            <EditIcon size={16} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteConfig(config.id)}
                          >
                            <TrashIcon size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="records" title="Salary Records">
          <Card className="shadow">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title>Salary Records</Card.Title>
                <div className="d-flex gap-2">
                  <Form.Select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(parseInt(e.target.value))}
                    style={{ width: "130px" }}
                  >
                    <option value="">All Months</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {getMonthName(i + 1)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(parseInt(e.target.value))}
                    style={{ width: "100px" }}
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 5 }, (_, i) => (
                      <option key={2023 + i} value={2023 + i}>
                        {2023 + i}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: "150px" }}
                  >
                    <option value="">All Status</option>
                    {Object.values(PAYMENT_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Period</th>
                    <th>Present Days</th>
                    <th>Working Days</th>
                    <th>Days on Leave</th>
                    <th>Total Hours</th>
                    <th>Gross Salary</th>
                    <th>Net Salary</th>
                    <th>Paid Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryRecords.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="13" className="text-center">
                        No salary records found
                      </td>
                    </tr>
                  ) : (
                    salaryRecords.map((record, index) => (
                      <tr key={record.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{record.userName}</strong>
                        </td>
                        <td>
                          <Badge
                            bg={
                              getUserRole(record.userId) === 'tutor' ? 'warning' :
                                'primary'
                            }
                          >
                            {getUserRole(record.userId)?.toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          {getMonthName(record.month)} {record.year}
                        </td>
                        <td>{record.presentDays || record.workingDays || 0}</td>
                        <td>{record.workingDays || 0}</td>
                        <td>
                          <Badge bg="warning">{record.daysOnLeave || 0}</Badge>
                        </td>
                        <td>{record.totalHours} hrs</td>
                        <td>{formatCurrency(record.grossSalary)}</td>
                        <td>{formatCurrency(record.netSalary)}</td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(record.totalPaid || 0)}
                          </strong>
                          {record.totalPaid > 0 && (
                            <div className="small text-muted">
                              Remaining: {formatCurrency((record.netSalary || 0) - (record.totalPaid || 0))}
                            </div>
                          )}
                        </td>
                        <td>{getStatusBadge(record.status)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record);
                                setPaymentFormData({
                                  amount: ((record.netSalary || 0) - (record.totalPaid || 0)).toString(),
                                  paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
                                  paymentDate: new Date().toISOString().split("T")[0],
                                  referenceNumber: "",
                                  notes: "",
                                });
                                setShowPaymentModal(true);
                              }}
                              disabled={record.status === PAYMENT_STATUS.PAID}
                            >
                              Pay
                            </Button>
                            <Button
                              variant="info"
                              size="sm"
                              onClick={() => handleViewPaymentHistory(record)}
                            >
                              History
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record);
                                setNewStatus(record.status);
                                setPaidAmount(record.netSalary?.toString() || "");
                                setShowStatusModal(true);
                              }}
                            >
                              Update
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="statistics" title="Statistics">
          <Row>
            <Col md={12}>
              {statistics && (
                <Row className="mb-4">
                  <Col md={3}>
                    <SalaryCard
                      title="Total Records"
                      value={statistics.totalRecords}
                      variant="primary"
                      icon="file-alt"
                    />
                  </Col>
                  <Col md={3}>
                    <SalaryCard
                      title="Total Paid"
                      value={statistics.totalPaid}
                      variant="success"
                      icon="check-circle"
                    />
                  </Col>
                  <Col md={3}>
                    <SalaryCard
                      title="Total Pending"
                      value={statistics.totalPending}
                      variant="warning"
                      icon="clock"
                    />
                  </Col>
                  <Col md={3}>
                    <SalaryCard
                      title="Avg Net Salary"
                      value={statistics.averageNetSalary}
                      variant="info"
                      icon="chart-bar"
                    />
                  </Col>
                </Row>
              )}

              {statistics && (
                <Card>
                  <Card.Body>
                    <Card.Title>Payment Status Breakdown</Card.Title>
                    <Row>
                      {Object.entries(statistics.statusBreakdown).map(([status, count]) => (
                        <Col md={2} key={status} className="text-center mb-3">
                          <div className="p-3 border rounded">
                            <h4>{count}</h4>
                            <small>{status.replace('_', ' ').toUpperCase()}</small>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Bulk Generate Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Generate Bulk Salary Records</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Month</Form.Label>
                  <Form.Select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {getMonthName(i + 1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Year</Form.Label>
                  <Form.Select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <option key={2023 + i} value={2023 + i}>
                        {2023 + i}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <p className="text-muted">
              This will generate salary records for all active users for{" "}
              {getMonthName(monthFilter)} {yearFilter}.
            </p>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleBulkGenerate} disabled={loading}>
            Generate Records
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Payment Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <Form>
              <div className="mb-3">
                <strong>Employee:</strong> {selectedRecord.userName}
                <br />
                <strong>Period:</strong> {getMonthName(selectedRecord.month)} {selectedRecord.year}
                <br />
                <strong>Net Salary:</strong> {formatCurrency(selectedRecord.netSalary)}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Payment Status</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {Object.values(PAYMENT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace('_', ' ').toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {newStatus === PAYMENT_STATUS.PAID && (
                <Form.Group className="mb-3">
                  <Form.Label>Paid Amount</Form.Label>
                  <Form.Control
                    type="number"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    placeholder="Enter paid amount"
                  />
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleStatusUpdate}>
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Salary Form Modal */}
      <SalaryForm
        show={showSalaryForm}
        onHide={() => {
          setShowSalaryForm(false);
          setEditingSalaryConfig(null);
        }}
        onSuccess={handleSalaryFormSuccess}
        editData={editingSalaryConfig}
      />

      {/* Payment Recording Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record Salary Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div>
              <Card className="mb-3 bg-light">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <strong>Employee:</strong> {selectedRecord.userName}<br />
                      <strong>Period:</strong> {getMonthName(selectedRecord.month)} {selectedRecord.year}<br />
                      <strong>Role:</strong> <Badge bg="info">{getUserRole(selectedRecord.userId)}</Badge>
                    </Col>
                    <Col md={6}>
                      <strong>Net Salary:</strong> {formatCurrency(selectedRecord.netSalary)}<br />
                      <strong>Total Paid:</strong> <span className="text-success">{formatCurrency(selectedRecord.totalPaid || 0)}</span><br />
                      <strong>Remaining:</strong> <span className="text-danger">{formatCurrency((selectedRecord.netSalary || 0) - (selectedRecord.totalPaid || 0))}</span>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Amount *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={paymentFormData.amount}
                        onChange={(e) =>
                          setPaymentFormData({ ...paymentFormData, amount: e.target.value })
                        }
                        placeholder="Enter amount"
                      />
                      <Form.Text className="text-muted">
                        Maximum: {formatCurrency((selectedRecord.netSalary || 0) - (selectedRecord.totalPaid || 0))}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method *</Form.Label>
                      <Form.Select
                        value={paymentFormData.paymentMethod}
                        onChange={(e) =>
                          setPaymentFormData({ ...paymentFormData, paymentMethod: e.target.value })
                        }
                      >
                        <option value={PAYMENT_METHODS.CASH}>Cash</option>
                        <option value={PAYMENT_METHODS.BANK_TRANSFER}>Bank Transfer</option>
                        <option value={PAYMENT_METHODS.UPI}>UPI</option>
                        <option value={PAYMENT_METHODS.CHEQUE}>Cheque</option>
                        <option value={PAYMENT_METHODS.CARD}>Card</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Date *</Form.Label>
                      <Form.Control
                        type="date"
                        value={paymentFormData.paymentDate}
                        onChange={(e) =>
                          setPaymentFormData({ ...paymentFormData, paymentDate: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reference Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={paymentFormData.referenceNumber}
                        onChange={(e) =>
                          setPaymentFormData({ ...paymentFormData, referenceNumber: e.target.value })
                        }
                        placeholder="Transaction ID / Cheque No."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={paymentFormData.notes}
                    onChange={(e) =>
                      setPaymentFormData({ ...paymentFormData, notes: e.target.value })
                    }
                    placeholder="Additional notes (optional)"
                  />
                </Form.Group>

                {paymentFormData.amount && (
                  <Card className="bg-info bg-opacity-10 border-info">
                    <Card.Body>
                      <h6>Payment Summary</h6>
                      <div className="d-flex justify-content-between">
                        <span>Current Payment:</span>
                        <strong>{formatCurrency(parseFloat(paymentFormData.amount) || 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Total Paid After:</span>
                        <strong className="text-success">
                          {formatCurrency((selectedRecord.totalPaid || 0) + (parseFloat(paymentFormData.amount) || 0))}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Remaining After:</span>
                        <strong className="text-danger">
                          {formatCurrency(
                            (selectedRecord.netSalary || 0) -
                            (selectedRecord.totalPaid || 0) -
                            (parseFloat(paymentFormData.amount) || 0)
                          )}
                        </strong>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleRecordPayment}>
            Record Payment
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Payment History Modal */}
      <Modal show={showPaymentHistoryModal} onHide={() => setShowPaymentHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payment History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div>
              <Card className="mb-3 bg-light">
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <strong>Employee:</strong> {selectedRecord.userName}
                    </Col>
                    <Col md={4}>
                      <strong>Period:</strong> {getMonthName(selectedRecord.month)} {selectedRecord.year}
                    </Col>
                    <Col md={4}>
                      <strong>Status:</strong> {getStatusBadge(selectedRecord.status)}
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col md={4}>
                      <strong>Net Salary:</strong> {formatCurrency(selectedRecord.netSalary)}
                    </Col>
                    <Col md={4}>
                      <strong>Total Paid:</strong> <span className="text-success">{formatCurrency(selectedRecord.totalPaid || 0)}</span>
                    </Col>
                    <Col md={4}>
                      <strong>Remaining:</strong> <span className="text-danger">{formatCurrency((selectedRecord.netSalary || 0) - (selectedRecord.totalPaid || 0))}</span>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {paymentHistory.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p>No payment records found</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Payment Method</th>
                      <th>Reference Number</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, index) => (
                      <tr key={payment.id}>
                        <td>{index + 1}</td>
                        <td>{payment.paymentDate?.toLocaleDateString()}</td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(payment.amount)}
                          </strong>
                        </td>
                        <td>
                          <Badge bg="primary">
                            {payment.paymentMethod.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td>{payment.referenceNumber || "-"}</td>
                        <td className="text-muted small">{payment.notes || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td colSpan="2" className="text-end"><strong>Total Paid:</strong></td>
                      <td colSpan="4">
                        <strong className="text-success">
                          {formatCurrency(paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0))}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}